import { WeekDay } from "Types/WeekDay";
import type { DayHours, HoursState, TimeRange } from "Types/OpeningHours";
import type { MerchantFacilityHoursDto } from "Infrastructure/Api/Api";
import { Resources, type TFunction } from "Translations/Resources";

/**
 * Convert backend weekday number (0=Sunday, 1=Monday, ... 6=Saturday)
 * to frontend WeekDay enum
 */
export const numberToWeekday = (num: number): WeekDay => {
  const mapping: Record<number, WeekDay> = {
    0: WeekDay.Sunday,
    1: WeekDay.Monday,
    2: WeekDay.Tuesday,
    3: WeekDay.Wednesday,
    4: WeekDay.Thursday,
    5: WeekDay.Friday,
    6: WeekDay.Saturday,
  };
  return mapping[num] ?? WeekDay.Monday;
};

/**
 * Convert frontend WeekDay enum to backend number
 * (0=Sunday, 1=Monday, ... 6=Saturday)
 */
export const weekdayToNumber = (day: WeekDay): number => {
  const mapping: Record<WeekDay, number> = {
    [WeekDay.Sunday]: 0,
    [WeekDay.Monday]: 1,
    [WeekDay.Tuesday]: 2,
    [WeekDay.Wednesday]: 3,
    [WeekDay.Thursday]: 4,
    [WeekDay.Friday]: 5,
    [WeekDay.Saturday]: 6,
  };
  return mapping[day];
};

/**
 * Create an empty TimeRange with a new UUID
 */
export const createEmptyTimeRange = (): TimeRange => ({
  id: crypto.randomUUID(),
  openTime: null,
  closeTime: null,
});

/**
 * Create empty DayHours for a specific weekday
 * Default state: closed all day with no ranges
 */
export const createEmptyDayHours = (weekday: WeekDay): DayHours => ({
  weekday,
  ranges: [],
  isOpenAllDay: false,
  isClosedAllDay: true,
});

/**
 * Initialize empty HoursState with all days set to closed
 */
export const createEmptyHoursState = (): HoursState => ({
  [WeekDay.Monday]: createEmptyDayHours(WeekDay.Monday),
  [WeekDay.Tuesday]: createEmptyDayHours(WeekDay.Tuesday),
  [WeekDay.Wednesday]: createEmptyDayHours(WeekDay.Wednesday),
  [WeekDay.Thursday]: createEmptyDayHours(WeekDay.Thursday),
  [WeekDay.Friday]: createEmptyDayHours(WeekDay.Friday),
  [WeekDay.Saturday]: createEmptyDayHours(WeekDay.Saturday),
  [WeekDay.Sunday]: createEmptyDayHours(WeekDay.Sunday),
});

/**
 * Convert backend DTO array to internal HoursState
 * Groups DTOs by weekday and creates TimeRange array for each day
 */
export const dtoToState = (dtos: MerchantFacilityHoursDto[]): HoursState => {
  // Start with all days closed
  const state = createEmptyHoursState();

  // Group DTOs by weekday
  const groupedByDay = dtos.reduce(
    (acc, dto) => {
      const weekday = numberToWeekday(dto.weekday);
      if (!acc[weekday]) {
        acc[weekday] = [];
      }
      acc[weekday].push(dto);
      return acc;
    },
    {} as Record<WeekDay, MerchantFacilityHoursDto[]>,
  );

  // Process each day
  for (const [weekdayStr, dayDtos] of Object.entries(groupedByDay)) {
    const weekday = weekdayStr as WeekDay;
    const firstDto = dayDtos[0];

    // Check if day has special state (open/closed all day)
    if (firstDto.isOpenAllDay) {
      state[weekday] = {
        weekday,
        ranges: [],
        isOpenAllDay: true,
        isClosedAllDay: false,
      };
    } else if (firstDto.isClosedAllDay) {
      state[weekday] = {
        weekday,
        ranges: [],
        isOpenAllDay: false,
        isClosedAllDay: true,
      };
    } else {
      // Convert each DTO to a TimeRange
      const ranges: TimeRange[] = dayDtos
        .filter((dto) => !dto.isOpenAllDay && !dto.isClosedAllDay)
        .map((dto) => ({
          id: crypto.randomUUID(),
          openTime: dto.openTime ?? null,
          closeTime: dto.closeTime ?? null,
        }));

      state[weekday] = {
        weekday,
        ranges,
        isOpenAllDay: false,
        isClosedAllDay: ranges.length === 0,
      };
    }
  }

  return state;
};

/**
 * Convert internal HoursState to backend DTO array
 * Flattens state: creates one DTO per time range per day
 */
export const stateToDto = (state: HoursState): MerchantFacilityHoursDto[] => {
  const dtos: MerchantFacilityHoursDto[] = [];

  for (const day of Object.values(WeekDay)) {
    const dayHours = state[day];
    const weekdayNumber = weekdayToNumber(day);

    if (dayHours.isOpenAllDay) {
      // Open all day: single DTO with null times
      dtos.push({
        weekday: weekdayNumber,
        openTime: null,
        closeTime: null,
        isOpenAllDay: true,
        isClosedAllDay: false,
      });
    } else if (dayHours.isClosedAllDay || dayHours.ranges.length === 0) {
      // Closed all day: single DTO with null times
      dtos.push({
        weekday: weekdayNumber,
        openTime: null,
        closeTime: null,
        isOpenAllDay: false,
        isClosedAllDay: true,
      });
    } else {
      // Normal hours: one DTO per time range
      for (const range of dayHours.ranges) {
        dtos.push({
          weekday: weekdayNumber,
          openTime: range.openTime,
          closeTime: range.closeTime,
          isOpenAllDay: false,
          isClosedAllDay: false,
        });
      }
    }
  }

  return dtos;
};

/**
 * Format time as HH:MM string
 */
const formatTime = (time: string | null): string => {
  if (!time) return "";
  // Time is in format "HH:MM:SS", we just need "HH:MM"
  return time.substring(0, 5);
};

/**
 * Normalize day hours by converting empty ranges to "closed all day" state
 * Used when saving to ensure consistent state representation
 */
export const normalizeEmptyRangesToClosed = (dayHours: DayHours): DayHours => {
  const hasValidRanges = dayHours.ranges.some(
    (range) => range.openTime && range.closeTime,
  );

  if (!hasValidRanges && !dayHours.isOpenAllDay && !dayHours.isClosedAllDay) {
    return {
      ...dayHours,
      isClosedAllDay: true,
      ranges: [],
    };
  }
  return dayHours;
};

/**
 * Format DayHours as a human-readable string
 * Examples:
 * - "8:00 - 16:00"
 * - "9:00 - 10:00, 11:00 - 15:00"
 * - "Open all day"
 * - "Closed all day"
 */
export const formatDayHours = (dayHours: DayHours, t: TFunction): string => {
  const resources = Resources.Components.Merchant.Facilities.Form.OpeningHours;

  if (dayHours.isOpenAllDay) {
    return t(resources.OpenAllDay);
  }

  if (dayHours.isClosedAllDay || dayHours.ranges.length === 0) {
    return t(resources.ClosedAllDay);
  }

  return dayHours.ranges
    .filter((range) => range.openTime && range.closeTime)
    .map(
      (range) =>
        `${formatTime(range.openTime)} - ${formatTime(range.closeTime)}`,
    )
    .join(", ");
};
